// Added by Sejune Oh on 2019-06-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef } from 'react'
import { RouteComponentProps } from 'react-router'
import { observable, action, computed, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import Intl from '@/helper/Intl'
import { routerPath, mgmtSubPath, ReactEmptyEvent } from '@/helper/define'
import Logger from '@/helper/Logger'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { MgmtFormBaseCProps } from '@/components/common/Template/MgmtFormTemplate'
import { UserStore, DisplableUserElementProps, UserCustomInfoProp } from '@/stores/mgmt/user'
import UserBase from './UserBase'
import { UserAssignedGroup } from './partial'
import { UserRouterParams } from '@/pages/setting/mgmt/users/UserRouter'
import { UserAuthStore } from '@/stores/user/UserAuth'
import { MgmtFormChildrenCProps } from '@/components/common/Template/MgmtFormTemplate/MgmtFormTemplate'
import { reqSetUserCustomInfos, reqDeleteUserCustomInfos } from '@/stores/mgmt/user/UserStore.service'
import _ from 'lodash'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// User 하위 Conmponent들이 공통으로 갖는 Props
export interface UserBaseCProps extends MgmtFormChildrenCProps {
  userId?: string
  userElement?: DisplableUserElementProps
}

interface UserBaseContainerCProps extends RouteComponentProps<UserRouterParams>, MgmtFormBaseCProps {
  user?: UserStore
  userAuth?: UserAuthStore
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default function UserBaseContainer({ type }: { type: 'add' | 'edit' }) {
  @inject('user', 'userAuth')
  @observer
  class UserBaseContainer extends Component<UserBaseContainerCProps, {}> {
    handleSubmit: ReactEmptyEvent = () => {}
    userId?: string
    userElement?: DisplableUserElementProps
    mounted?: boolean = false
    mountedChildComponents: Array<string> = []

    _userPath = `/${routerPath.MGMT}/${mgmtSubPath.USERS}`
    _userFormRef = createRef<any>()
    _userAssignedGroupRef = createRef<UserAssignedGroup>()
    _userCtrRef = createRef<any>()
    _userCustomInfoRef = createRef<any>()

    constructor(props: UserBaseContainerCProps) {
      super(props)
      makeObservable(this, {
        handleSubmit: observable,
        userId: observable,
        userElement: observable,
        mounted: observable,
        mountedChildComponents: observable,
        componentDidMount: action,
        _setPath: action,
        _editFormTypeInit: action,
        onChildComponentMount: action,
        userAuth: computed,
      })
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    componentDidMount = async () => {
      try {
        const isAddFormType = type === 'add'
        this.handleSubmit = isAddFormType ? this._saveUserElement : this._updateUserElement

        await this._setPath(isAddFormType)

        if (!isAddFormType) await this._editFormTypeInit()

        this.mounted = true
      } catch (err) {
        Logger.errLog('Error > User 등록/수정을 위한 데이터 수집중')
        console.log(err)
      }
    }

    render() {
      return (
        this.mounted && (
          <UserBase
            {...this.props}
            userId={this.userId}
            userFormRef={this._userFormRef}
            userAssignedGroupRef={this._userAssignedGroupRef}
            onCacelClick={this._handleCancel}
            onSubmitClick={this.handleSubmit}
            onRemoveClick={this._handleRemove}
            onChildComponentMount={this.onChildComponentMount}
            userElement={this.userElement}
            userCtrRef={this._userCtrRef}
            userCustomInfoRef={this._userCustomInfoRef}
          />
        )
      )
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    get userAuth() {
      return this.props.userAuth!
    }

    _setPath = (isAddFormType: boolean) => {
      const breadCrumbText = Intl.getIntlText({ id: 'htmljs_react.mgmt.userbasecontainer.bread_crumb_text' })
      let pathSet: [string, string] = ['', `${this.props.match.url}`]
      pathSet[0] = isAddFormType
        ? Intl.getIntlText({ id: 'htmljs_react.mgmt.userbasecontainer.title_add_template' })
        : Intl.getIntlText({ id: 'htmljs_react.mgmt.userbasecontainer.title_edit_template' })
      this.props.setCurPath([[breadCrumbText, this._userPath], pathSet])
    }

    _editFormTypeInit = async () => {
      const userId = this.props.match.params.userId

      // Get UserElement
      try {
        this.userElement = await this.props.user!.getElement(userId!)
      } catch (err) {
        // prettier-ignore
        const error = Intl.getIntlTextWithOption({ id: 'htmljs_react.mgmt.userbasecontainer.msg_user_not_found' }, { userId })
        this.props.showNotFoundDialog(error)
        throw new Error(error)
      }

      this.userId = userId
    }

    onChildComponentMount = (key: string) => {
      Logger.devLog(`[Mgmt / User] ${key} is Mounted`)
      this.mountedChildComponents.push(key)

      if (this.mountedChildComponents.length === 4) {
        this.props.onMounted()
      }
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////q
    _handleRemove = () => {
      if (this.userElement!.id === '1') {
        // prettier-ignore
        const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userbasecontainer.noti_can_not_remove_admin' })
        this.props.showSnackbar('err', eMessage)
        return
      }

      if (this.userAuth.userInfo!.id!.toString() === this.userElement!.id!.toString()) {
        // prettier-ignore
        const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userbasecontainer.noti_can_not_remove_current' })
        this.props.showSnackbar('err', eMessage)
        return
      }

      this.props.showRemoveDialog(this.props.user!.removeElement, this.userElement!.id!)
    }

    _handleCancel = () => {
      this.props.movePrevPath()
    }

    /** User 생성 */
    _saveUserElement: ReactEmptyEvent = async () => {
      try {
        this.props.handleLoadingCover(true)

        // @ts-ignore
        const userElement = this._userFormRef.current!.getFormData()
        if (!userElement) return

        // @ts-ignore
        const assignedGroupIds = this._userAssignedGroupRef.current!.getFormData()
        if (!assignedGroupIds) return

        if (userElement.expired_at === '0') delete userElement.expired_at
        else userElement.expired_at = moment(userElement.expired_at).toISOString()

        const newUserElement = await this.props.user!.addElement(userElement as DisplableUserElementProps)
        await this.props.user!.updateAssignedGroups(newUserElement.id!, assignedGroupIds)

        // Custom info 저장.
        const customInfos = this._userCustomInfoRef.current!.getFormData()
        if (customInfos && customInfos.length !== 0) {
          await reqSetUserCustomInfos(newUserElement.id!, customInfos)
        }

        Logger.devLog(`User Added => ${newUserElement.id}`)
        this.props.movePrevPath()
      } catch (err) {
        Logger.errLog(err)
        this.props.showSnackbar('err', err)
      } finally {
        this.props.handleLoadingCover(false)
      }
    }
    /** User 업데이트 */
    _updateUserElement: ReactEmptyEvent = async () => {
      try {
        let message = ''
        this.props.handleLoadingCover(true)

        // @ts-ignore
        const userElement = this._userFormRef.current!.getFormData()
        if (!userElement) return

        // @ts-ignore
        const assignedGroupIds = this._userAssignedGroupRef.current!.getFormData()
        if (!assignedGroupIds) return

        const userActivated = this._userCtrRef.current!.getActivationStatus()
        const userAuth2Enabled = this._userCtrRef.current!.getAuth2Status()

        const languageUpdated = userElement.ui_language !== this.userElement!.ui_language
        const updatedUserElement = { ...this.userElement, ...userElement, activated: userActivated, auth2_enabled: userAuth2Enabled }

        // expired at 변환 처리
        if (userElement.expired_at === '0') delete updatedUserElement.expired_at
        else updatedUserElement.expired_at = moment(userElement.expired_at).toISOString()

        await this.props.user!.updateElement(this.userId!, updatedUserElement)
        await this.props.user!.updateAssignedGroups(this.userId!, assignedGroupIds)

        // Custom info 저장.
        const customInfos = this._userCustomInfoRef.current!.getFormData()
        if (this.userElement!.user_custom_infos || customInfos) {
          const prevCustomInfos = this.userElement!.user_custom_infos
            ? [...this.userElement!.user_custom_infos.map(({ key, value }) => ({ key, value }))]
            : []
          const prevCustomInfoKeys = prevCustomInfos.map(({ key }) => key)

          // 현재 등록된 Custom Infos가 없음 -> 이전 키 삭제.
          if (customInfos.length === 0) {
            await reqDeleteUserCustomInfos(this.userId!, prevCustomInfoKeys)
          } else {
            // 이전에 CustomInfos를 등록한 적 없고, 새로운 Custom Infos를 등록하려고함 -> 신규 업데이트.
            if (!prevCustomInfoKeys || prevCustomInfoKeys.length === 0) {
              await reqSetUserCustomInfos(this.userId!, customInfos)
            } else {
              // 이전에 사용중이었던 키도 있고, 신규 키도 있음.
              const curCustomInfoKeys = (customInfos as UserCustomInfoProp[]).map(({ key }) => key)
              const removedKeys = _.difference(prevCustomInfoKeys, curCustomInfoKeys)
              await reqDeleteUserCustomInfos(this.userId!, removedKeys)
              await reqSetUserCustomInfos(this.userId!, customInfos)
            }
          }
        }

        Logger.devLog(`User Updated => ${this.userId}`)

        if (this.userAuth.userInfo!.username === this.userElement!.username) {
          if (languageUpdated) {
            // prettier-ignore
            message = Intl.getIntlText({ id: 'htmljs_react.mgmt.userbasecontainer.noti_lang_change_saved' })
            this.props.showRefreshDialog(message)
            return
          }

          this.userAuth.updateUserInfo(updatedUserElement)
        }

        // prettier-ignore
        message = Intl.getIntlText({ id: 'htmljs_react.mgmt.userbasecontainer.noti_change_saved' })
        this.props.showSnackbar('info', message)
      } catch (err) {
        Logger.errLog(err)
        this.props.showSnackbar('err', err)
      } finally {
        this.props.handleLoadingCover(false)
      }
    }
  }

  return UserBaseContainer
}
